import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ChevronDownIcon, CheckCircleIcon, XCircleIcon, ExclamationCircleIcon, InformationCircleIcon } from "@heroicons/react/24/solid";
import { UploadIcon } from "lucide-react";
import { authSelectors } from "../../store/auth/selector";
import { actions } from "../../store/auth/slice";
import {
  AccountFragmentFragment,
  QuestionPriority,
  QuestionType,
  TteAdjustmentFactor,
  TteDealOnboardingStep,
  TteValuationStatus,
  useAccountQuery,
  useCreateDataRoomFileMutation,
  useCreateQuestionAnswerMutation,
  useDataRoomFastQuery,
  useSellerDashboardQuery,
  useS3MultipartUploadCompleteMutation,
  useTteDealDataQuery,
  useTteIndustriesQuery,
  useUpdateTteDealDataMutation,
} from "../../graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { Uploader, getNumberOfParts } from "../../utils/multipartUploader";
import { TextInput } from "../../components/tailwind/TextInput";
import { TextArea } from "../../components/tailwind/TextArea";
import { Button } from "../../components/tailwind/Button";
import Dropdown, { Option } from "../../components/tailwind/Dropdown";
import { AnimatedModal } from "../../components/AnimatedModal";

import {
  computeHighPriorityRatio,
  computeReadinessScore,
  computeWeightedDdqlRatio,
  fmt,
  fmtM,
  PRIORITY_WEIGHTS,
  readinessLabel,
  Section,
  SectionQuestion,
} from "@/src/utils/dealReadiness";

type DealBarSentiment = "default" | "positive" | "negative";

function useAnimatedNumber(target: number | null, duration = 700): number | null {
  const [display, setDisplay] = useState<number | null>(target);
  const currentRef = useRef<number | null>(target);

  useEffect(() => {
    if (target === null) {
      setDisplay(null);
      currentRef.current = null;
      return;
    }
    const from = currentRef.current ?? target;
    if (from === target) {
      setDisplay(target);
      currentRef.current = target;
      return;
    }
    const start = performance.now();
    let rafId = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = from + (target - from) * eased;
      currentRef.current = next;
      setDisplay(next);
      if (t < 1) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [target, duration]);

  return display;
}

// ---------------------------------------------------------------------------
// Onboarding option lists
// ---------------------------------------------------------------------------


const US_STATE_OPTIONS: Option[] = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
  "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
  "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi",
  "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey",
  "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma",
  "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
  "West Virginia", "Wisconsin", "Wyoming", "Washington D.C.",
].map((v) => ({ label: v, value: v }));

const PRICING_MODEL_OPTIONS: Option[] = [
  { label: "Subscription / SaaS", value: "Subscription" },
  { label: "One-time license", value: "One-time" },
  { label: "Usage-based", value: "Usage-based" },
  { label: "Freemium", value: "Freemium" },
  { label: "Transaction fee", value: "Transaction fee" },
  { label: "Project-based", value: "Project-based" },
  { label: "Other", value: "Other" },
];

// ---------------------------------------------------------------------------
// Valuation bar components
// ---------------------------------------------------------------------------

function EbitdaBar({
  multLow,
  multHigh,
  sentiment = "default",
}: {
  multLow: number;
  multHigh: number;
  sentiment?: DealBarSentiment;
}) {
  const scale = 20;
  const leftPct = Math.max(0, Math.min(100, (multLow / scale) * 100));
  const widthPct = Math.max(0, Math.min(100 - leftPct, ((multHigh - multLow) / scale) * 100));

  const barColor =
    sentiment === "positive" ? "bg-green-500"
    : sentiment === "negative" ? "bg-amber-500"
    : "bg-persian-500";

  return (
    <div className="mt-3">
      <div className="relative h-1.5 bg-gray-100 rounded-full">
        <div
          className={`absolute h-full rounded-full ${barColor}`}
          style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
        />
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-[10px] text-gray-300">0x</span>
        <span className="text-[10px] text-gray-300">10x</span>
        <span className="text-[10px] text-gray-300">20x</span>
      </div>
    </div>
  );
}

function IndustryComparisonBars({
  industryLow,
  industryHigh,
  dealLow,
  dealHigh,
  sentiment = "default",
}: {
  industryLow: number;
  industryHigh: number;
  dealLow: number;
  dealHigh: number;
  sentiment?: DealBarSentiment;
}) {
  const scale = Math.max(20, industryHigh * 1.5, dealHigh * 1.5);
  const leftPct = (v: number) => `${Math.max(0, Math.min(100, (v / scale) * 100)).toFixed(1)}%`;
  const widthPct = (lo: number, hi: number) =>
    `${Math.max(0, Math.min(100, ((hi - lo) / scale) * 100)).toFixed(1)}%`;

  const dealBg =
    sentiment === "positive" ? "bg-green-500"
    : sentiment === "negative" ? "bg-amber-500"
    : "bg-persian-500";
  const dealText =
    sentiment === "positive" ? "text-green-700"
    : sentiment === "negative" ? "text-amber-700"
    : "text-persian-600";

  return (
    <div className="space-y-3">
      <div>
        <div className="flex justify-between items-center mb-1.5">
          <span className="text-xs text-gray-400">Industry</span>
          <span className="text-xs text-gray-400 font-medium">
            {industryLow.toFixed(1)}x – {industryHigh.toFixed(1)}x
          </span>
        </div>
        <div className="relative h-2 bg-gray-100 rounded-full">
          <div
            className="absolute h-full bg-gray-300 rounded-full"
            style={{ left: leftPct(industryLow), width: widthPct(industryLow, industryHigh) }}
          />
        </div>
      </div>
      <div>
        <div className="flex justify-between items-center mb-1.5">
          <span className={`text-xs font-medium ${dealText}`}>Your deal</span>
          <span className={`text-xs font-medium ${dealText}`}>
            {dealLow.toFixed(1)}x – {dealHigh.toFixed(1)}x
          </span>
        </div>
        <div className="relative h-2 bg-gray-100 rounded-full">
          <div
            className={`absolute h-full rounded-full ${dealBg}`}
            style={{ left: leftPct(dealLow), width: widthPct(dealLow, dealHigh) }}
          />
        </div>
      </div>
      <div className="flex items-center gap-4 mt-1">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-2 bg-gray-300 rounded-sm" />
          <span className="text-[10px] text-gray-400">Industry range</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className={`w-3 h-2 rounded-sm ${dealBg}`} />
          <span className="text-[10px] text-gray-400">Your range</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Priority styles
// ---------------------------------------------------------------------------

const PRIORITY_STYLES: Record<string, { badge: string }> = {
  [QuestionPriority.Urgent]: { badge: "bg-red-100 text-red-600" },
  [QuestionPriority.High]:   { badge: "bg-amber-100 text-amber-600" },
  [QuestionPriority.Medium]: { badge: "bg-yellow-100 text-yellow-600" },
  [QuestionPriority.Low]:    { badge: "bg-gray-100 text-gray-500" },
  [QuestionPriority.None]:   { badge: "bg-gray-50 text-gray-400" },
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type UploadingFile = {
  id: string;
  name: string;
  progress: number;
  status: "uploading" | "complete" | "error";
};

// ---------------------------------------------------------------------------
// Onboarding: progress bar
// ---------------------------------------------------------------------------

function OnboardingProgressBar({ step }: { step: 1 | 2 }) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-gray-700">Setting up your deal</p>
        <p className="text-sm text-gray-400">Step {step} of 2</p>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-persian-500 rounded-full transition-all duration-500"
          style={{ width: step === 1 ? "50%" : "100%" }}
        />
      </div>
      <div className="flex justify-between mt-2">
        <span className="text-xs font-semibold text-persian-600">Business details</span>
        <span className={`text-xs font-semibold ${step >= 2 ? "text-persian-600" : "text-gray-300"}`}>
          Historical data
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Onboarding: step 1 form
// ---------------------------------------------------------------------------

function Step1Form({ tteId, onDone }: { tteId: string; onDone: () => void }) {
  const client = useGqlClient();
  const mutation = useUpdateTteDealDataMutation(client);
  const industriesQuery = useTteIndustriesQuery(client);

  const industryOptions: Option[] = (industriesQuery.data?.tteIndustries ?? []).map((ind) => ({
    label: ind.industry,
    value: ind.id,
  }));

  const [industry, setIndustry] = useState<Option | null>(null);
  const [state, setState] = useState<Option | null>(null);
  const [revenue, setRevenue] = useState("");
  const [ebitda, setEbitda] = useState("");
  const [yoyGrowth, setYoyGrowth] = useState("");
  const [website, setWebsite] = useState("");

  const isValid = !!industry && !!state && revenue !== "" && ebitda !== "" && yoyGrowth !== "";

  async function handleSubmit() {
    if (!isValid) return;
    await mutation.mutateAsync({
      input: {
        id: tteId,
        industryId: industry!.value!,
        state: state!.value!,
        revenue: Math.round(parseFloat(revenue)),
        ebitda: Math.round(parseFloat(ebitda)),
        yoyGrowth: parseFloat(yoyGrowth),
        website: website || undefined,
        onboardingStep: TteDealOnboardingStep.Step_2,
      },
    });
    onDone();
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 space-y-5">
      <div>
        <h2 className="text-base font-bold text-gray-900">Business details</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Tell us about your business to generate a valuation.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Dropdown
            label="Industry"
            options={industryOptions}
            selectedOption={industry ?? undefined}
            onSelect={setIndustry}
            searchable
          />
        </div>
        <div>
          <Dropdown
            label="State"
            options={US_STATE_OPTIONS}
            selectedOption={state ?? undefined}
            onSelect={setState}
            searchable
          />
        </div>
        <div>
          <TextInput
            label="Annual revenue (USD)"
            type="number"
            value={revenue}
            onChange={(e) => setRevenue(e.currentTarget.value)}
            placeholder="e.g. 2000000"
          />
        </div>
        <div>
          <TextInput
            label="EBITDA (USD)"
            type="number"
            value={ebitda}
            onChange={(e) => setEbitda(e.currentTarget.value)}
            placeholder="e.g. 500000"
          />
        </div>
        <div>
          <TextInput
            label="YoY growth (%)"
            type="number"
            value={yoyGrowth}
            onChange={(e) => setYoyGrowth(e.currentTarget.value)}
            placeholder="e.g. 25"
          />
        </div>
        <div>
          <TextInput
            label="Company website (optional)"
            value={website}
            onChange={(e) => setWebsite(e.currentTarget.value)}
            placeholder="https://yourcompany.com"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          text="Next: Historical data"
          variant="positive"
          onClick={handleSubmit}
          isDisabled={!isValid}
          isLoading={mutation.isPending}
          loadingText="Saving..."
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Onboarding: step 2 form
// ---------------------------------------------------------------------------

function Step2Form({ tteId, onDone }: { tteId: string; onDone: () => void }) {
  const client = useGqlClient();
  const mutation = useUpdateTteDealDataMutation(client);

  const [revenuePrevYear1, setRevenuePrevYear1] = useState("");
  const [revenuePrevYear2, setRevenuePrevYear2] = useState("");
  const [yearsInOperation, setYearsInOperation] = useState("");
  const [employeeCount, setEmployeeCount] = useState("");
  const [pricingModel, setPricingModel] = useState<Option | null>(null);

  const isValid =
    revenuePrevYear1 !== "" &&
    revenuePrevYear2 !== "" &&
    yearsInOperation !== "" &&
    employeeCount !== "" &&
    !!pricingModel;

  async function handleSubmit() {
    if (!isValid) return;
    await mutation.mutateAsync({
      input: {
        id: tteId,
        revenuePrevYear1: Math.round(parseFloat(revenuePrevYear1)),
        revenuePrevYear2: Math.round(parseFloat(revenuePrevYear2)),
        yearsInOperation: Math.round(parseFloat(yearsInOperation)),
        employeeCount: Math.round(parseFloat(employeeCount)),
        pricingModel: pricingModel!.value!,
        onboardingStep: TteDealOnboardingStep.Step_3,
      },
    });
    onDone();
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 space-y-5">
      <div>
        <h2 className="text-base font-bold text-gray-900">Historical data</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Help us understand your business trajectory.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <TextInput
            label="Revenue — previous year 1 (USD)"
            type="number"
            value={revenuePrevYear1}
            onChange={(e) => setRevenuePrevYear1(e.currentTarget.value)}
            placeholder="e.g. 1500000"
          />
        </div>
        <div>
          <TextInput
            label="Revenue — previous year 2 (USD)"
            type="number"
            value={revenuePrevYear2}
            onChange={(e) => setRevenuePrevYear2(e.currentTarget.value)}
            placeholder="e.g. 1000000"
          />
        </div>
        <div>
          <TextInput
            label="Years in operation"
            type="number"
            value={yearsInOperation}
            onChange={(e) => setYearsInOperation(e.currentTarget.value)}
            placeholder="e.g. 5"
          />
        </div>
        <div>
          <TextInput
            label="Number of employees"
            type="number"
            value={employeeCount}
            onChange={(e) => setEmployeeCount(e.currentTarget.value)}
            placeholder="e.g. 20"
          />
        </div>
        <div className="sm:col-span-2">
          <Dropdown
            label="Pricing model"
            options={PRICING_MODEL_OPTIONS}
            selectedOption={pricingModel ?? undefined}
            onSelect={setPricingModel}
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          text="Complete setup"
          variant="positive"
          onClick={handleSubmit}
          isDisabled={!isValid}
          isLoading={mutation.isPending}
          loadingText="Saving..."
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Answer modal (question text + document upload)
// ---------------------------------------------------------------------------

function AnswerModal({
  question,
  rootFolderId,
  open,
  onClose,
  onDone,
}: {
  question: SectionQuestion | null;
  rootFolderId: string | undefined;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const client = useGqlClient();
  const answerMutation = useCreateQuestionAnswerMutation(client);
  const createFileMutation = useCreateDataRoomFileMutation(client);
  const completeMutation = useS3MultipartUploadCompleteMutation(client);

  const [textAnswer, setTextAnswer] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [uploadState, setUploadState] = useState<UploadingFile | null>(null);

  const isDoc = question?.type === QuestionType.DocumentRequest;

  function reset() {
    setTextAnswer("");
    setUploadState(null);
    setDragOver(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleTextSubmit() {
    if (!question || !textAnswer.trim()) return;
    await answerMutation.mutateAsync({
      questionId: question.id,
      answer: textAnswer.trim(),
      dataRoomFileIds: [],
      answerFiles: [],
    });
    reset();
    onDone();
  }

  async function handleFileUpload(file: File) {
    if (!question || !rootFolderId) return;
    const tempId = crypto.randomUUID();
    setUploadState({ id: tempId, name: file.name, progress: 0, status: "uploading" });
    try {
      const result = await createFileMutation.mutateAsync({
        name: file.name,
        description: "",
        dataRoomFolderID: rootFolderId,
        permissionIDs: [],
        fileName: file.name,
        fileType: file.type || "application/octet-stream",
        parts: getNumberOfParts(file),
      });
      const { dataRoomFile, initialVersion, multiPartUploadID, parts } =
        result.createDataRoomFile;

      await new Promise<void>((resolve, reject) => {
        const uploader = new Uploader({
          file,
          uploadId: multiPartUploadID,
          parts: parts.map((p) => ({ PartNumber: p.partNumber, signedUrl: p.presignedUrl })),
        });
        uploader
          .onProgress(({ percentage }) =>
            setUploadState((s) => s ? { ...s, progress: percentage } : s)
          )
          .onComplete(async (uploadedParts) => {
            await completeMutation.mutateAsync({
              dataRoomFileID: dataRoomFile.id,
              dataRoomFileVersionID: initialVersion.id,
              multiPartUploadID,
              parts: uploadedParts.map((p) => ({ partNumber: p.PartNumber, etag: p.ETag })),
            });
            resolve();
          })
          .onError(reject);
        uploader.start();
      });

      await answerMutation.mutateAsync({
        questionId: question.id,
        answer: "",
        dataRoomFileIds: [dataRoomFile.id],
        answerFiles: [],
      });

      setUploadState((s) => s ? { ...s, status: "complete", progress: 100 } : s);
      setTimeout(() => {
        reset();
        onDone();
      }, 600);
    } catch {
      setUploadState((s) => s ? { ...s, status: "error" } : s);
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
    e.target.value = "";
  }

  return (
    <AnimatedModal open={open} onClose={handleClose} size="md" padding="p-0">
      <AnimatedModal.Header title={question?.title ?? ""} onClose={handleClose} />
      <div className="p-6">
        {isDoc ? (
          <div className="space-y-4">
            {uploadState ? (
              <div className="flex items-center gap-3 p-4 rounded-lg bg-gray-50 border border-gray-100">
                {uploadState.status === "complete" ? (
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                ) : uploadState.status === "error" ? (
                  <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
                ) : (
                  <div className="w-5 h-5 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700 truncate font-medium">{uploadState.name}</p>
                  {uploadState.status === "uploading" && (
                    <div className="mt-1.5 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                      <div
                        className="h-full bg-persian-500 rounded-full transition-all duration-300"
                        style={{ width: `${uploadState.progress}%` }}
                      />
                    </div>
                  )}
                  {uploadState.status === "error" && (
                    <p className="text-xs text-red-500 mt-0.5">Upload failed — please try again</p>
                  )}
                </div>
                {uploadState.status === "uploading" && (
                  <span className="text-xs text-gray-400 flex-shrink-0">{uploadState.progress}%</span>
                )}
              </div>
            ) : (
              <label
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                className={`flex flex-col items-center justify-center gap-3 border-2 border-dashed rounded-xl py-10 px-6 cursor-pointer transition-colors ${
                  dragOver
                    ? "border-persian-400 bg-persian-50"
                    : "border-gray-200 hover:border-persian-300 hover:bg-gray-50"
                }`}
              >
                <UploadIcon className={`w-6 h-6 ${dragOver ? "text-persian-500" : "text-gray-300"}`} />
                <div className="text-center">
                  <p className={`text-sm font-semibold ${dragOver ? "text-persian-600" : "text-gray-500"}`}>
                    Drop a file or{" "}
                    <span className="text-persian-500">browse</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">Any file type accepted</p>
                </div>
                <input type="file" className="hidden" onChange={handleInputChange} />
              </label>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <TextArea
              name="answer"
              rows={5}
              value={textAnswer}
              onChange={(e) => setTextAnswer(e.currentTarget.value)}
              placeholder="Type your answer here…"
            />
            <div className="flex justify-end">
              <Button
                text="Submit answer"
                variant="positive"
                onClick={handleTextSubmit}
                isDisabled={!textAnswer.trim()}
                isLoading={answerMutation.isPending}
                loadingText="Submitting…"
              />
            </div>
          </div>
        )}
      </div>
    </AnimatedModal>
  );
}

// ---------------------------------------------------------------------------
// Adjustment insights
// ---------------------------------------------------------------------------

const FACTOR_LABELS: Record<TteAdjustmentFactor, string> = {
  [TteAdjustmentFactor.CustomerConcentration]: "Customer Concentration",
  [TteAdjustmentFactor.RecurringRevenue]: "Recurring Revenue",
  [TteAdjustmentFactor.RevenueGrowth]: "Revenue Growth",
  [TteAdjustmentFactor.EbitdaMargin]: "EBITDA Margin",
  [TteAdjustmentFactor.OwnerDependency]: "Owner Dependency",
  [TteAdjustmentFactor.ManagementTeam]: "Management Team",
  [TteAdjustmentFactor.Seasonality]: "Seasonality",
  [TteAdjustmentFactor.FinancialQuality]: "Financial Quality",
  [TteAdjustmentFactor.WorkingCapital]: "Working Capital",
  [TteAdjustmentFactor.LegalRegulatory]: "Legal & Regulatory",
  [TteAdjustmentFactor.IpProprietary]: "IP & Proprietary Assets",
  [TteAdjustmentFactor.LeaseRealEstate]: "Lease & Real Estate",
};

const FACTOR_DESCRIPTIONS: Record<TteAdjustmentFactor, string> = {
  [TteAdjustmentFactor.CustomerConcentration]:
    "Customer concentration measures how reliant your revenue is on a small group of customers. Buyers pay close attention because losing one major account post-close can materially impact earnings. Diversified revenue typically commands higher multiples.",
  [TteAdjustmentFactor.RecurringRevenue]:
    "Recurring revenue — contracted or subscription-based income — is one of the single biggest drivers of valuation multiples in M&A. Predictable, repeatable revenue reduces buyer risk and often justifies a significant premium over transactional models.",
  [TteAdjustmentFactor.RevenueGrowth]:
    "Revenue growth signals market traction and future cash flow potential. Acquirers price in growth trajectory heavily, especially when it's consistent and backed by a clear strategy. Flat or declining revenue compresses multiples quickly.",
  [TteAdjustmentFactor.EbitdaMargin]:
    "EBITDA margin reflects operational efficiency and pricing power. Strong margins relative to industry peers suggest a defensible moat, which buyers reward with higher multiples. Thin margins invite scrutiny on cost structure.",
  [TteAdjustmentFactor.OwnerDependency]:
    "Owner dependency captures how much of the business relies on the founder or key principals. High dependency is a major concern — buyers worry the business cannot operate without them. A documented second layer of management materially de-risks the deal.",
  [TteAdjustmentFactor.ManagementTeam]:
    "A strong, committed management team is one of the most valuable assets in any transaction. Buyers pay premiums for teams willing to stay post-close and execute growth plans. Gaps in leadership create execution risk that gets priced into the offer.",
  [TteAdjustmentFactor.Seasonality]:
    "Seasonality impacts working capital planning and cash flow predictability. Heavily seasonal businesses face harder scrutiny during diligence around inventory, staffing, and revenue recognition — and often see multiple discounts applied.",
  [TteAdjustmentFactor.FinancialQuality]:
    "Clean, audited financials with clear accounting policies dramatically reduce diligence friction. Buyers apply discounts — or walk — when financials are messy, inconsistent, or lack supporting documentation. Quality of earnings reports surface these issues quickly.",
  [TteAdjustmentFactor.WorkingCapital]:
    "Working capital is almost always a negotiated item at close. A well-managed working capital position — collected receivables, clean inventory, manageable payables — prevents purchase price adjustments that can erode deal value after signing.",
  [TteAdjustmentFactor.LegalRegulatory]:
    "Open legal disputes, compliance gaps, or regulatory uncertainty create tangible deal risk. Buyers typically respond with indemnities, escrow holdbacks, or valuation discounts. Resolving these before going to market protects multiple.",
  [TteAdjustmentFactor.IpProprietary]:
    "Proprietary IP — patents, trade secrets, proprietary technology — creates defensibility that strategic buyers reward. Unclear ownership, missing assignments, or weak protection around core IP significantly undermines the asset's strategic value.",
  [TteAdjustmentFactor.LeaseRealEstate]:
    "Lease terms and real estate commitments become part of the deal economics. Unfavorable lease terms, upcoming renewals, or sale-leaseback opportunities all factor into how a buyer prices the business. Clean, transferable leases are materially preferred.",
};

function deltaDisplay(deltaLow: number, deltaHigh: number): string {
  const fmt = (n: number) => {
    const abs = Math.abs(n).toFixed(1);
    return n >= 0 ? `+${abs}x` : `-${abs}x`;
  };
  if (Math.abs(deltaLow - deltaHigh) < 0.05) return fmt(deltaLow);
  return `${fmt(deltaLow)} – ${fmt(deltaHigh)}`;
}

type DealAdjustment = {
  id: string;
  factor: TteAdjustmentFactor;
  deltaLow: number;
  deltaHigh: number;
  reasoning: string;
};

function AdjustmentPill({
  adjustment,
  isOpen,
  onToggle,
}: {
  adjustment: DealAdjustment;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const avg = (adjustment.deltaLow + adjustment.deltaHigh) / 2;
  const isPositive = avg > 0;
  const label = FACTOR_LABELS[adjustment.factor] ?? adjustment.factor;

  return (
    <button
      onClick={onToggle}
      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50 transition-colors"
    >
      {isPositive ? (
        <CheckCircleIcon className="w-5 h-5 text-green-800 flex-shrink-0" />
      ) : (
        <ExclamationCircleIcon className="w-5 h-5 text-red-800 flex-shrink-0" />
      )}
      {label}
      <ChevronDownIcon
        className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${
          isOpen ? "rotate-180" : ""
        }`}
      />
    </button>
  );
}

function KeyFactorsCard({ adjustments }: { adjustments: DealAdjustment[] }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const visible = adjustments.filter((a) => (a.deltaLow + a.deltaHigh) / 2 !== 0);
  if (visible.length === 0) return null;

  const openAdj = openId ? (visible.find((a) => a.id === openId) ?? null) : null;
  const openIsPositive = openAdj ? (openAdj.deltaLow + openAdj.deltaHigh) / 2 > 0 : true;

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
          Key Factors
        </p>
        {visible.map((adj) => (
          <AdjustmentPill
            key={adj.id}
            adjustment={adj}
            isOpen={adj.id === openId}
            onToggle={() => setOpenId((id) => (id === adj.id ? null : adj.id))}
          />
        ))}
      </div>

      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          openAdj ? "max-h-[40rem] mt-4" : "max-h-0"
        }`}
      >
        {openAdj && (
          <div className={`rounded-xl p-6 ${openIsPositive ? "bg-emerald-50" : "bg-red-50"}`}>
            <p className={`text-base font-bold mb-1 ${openIsPositive ? "text-emerald-900" : "text-red-900"}`}>
              {FACTOR_LABELS[openAdj.factor]}
            </p>
            <p className={`text-xs font-semibold uppercase tracking-wider mb-4 ${openIsPositive ? "text-emerald-700" : "text-red-700"}`}>
              {openIsPositive ? "Strength · Supports your valuation" : "Concern · May reduce your valuation"}
            </p>

            <p className={`text-sm leading-relaxed ${openIsPositive ? "text-emerald-900" : "text-red-900"}`}>
              {FACTOR_DESCRIPTIONS[openAdj.factor]}
            </p>

            {(openAdj.deltaLow !== 0 || openAdj.deltaHigh !== 0) && (
              <div className="mt-4 inline-block bg-white rounded-lg px-4 py-2.5 border border-gray-100">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  {openIsPositive ? "Multiple uplift" : "Multiple impact"}
                </p>
                <p className={`text-sm font-bold mt-0.5 ${openIsPositive ? "text-emerald-900" : "text-red-900"}`}>
                  {deltaDisplay(openAdj.deltaLow, openAdj.deltaHigh)} EBITDA
                </p>
              </div>
            )}

            {openAdj.reasoning && (
              <p className={`mt-4 text-sm leading-relaxed ${openIsPositive ? "text-emerald-900" : "text-red-900"}`}>
                <span className="font-semibold">Your situation: </span>{openAdj.reasoning}
              </p>
            )}

            <button className="mt-4 text-sm font-bold text-blue-600 hover:text-blue-700 transition-colors">
              {openIsPositive ? "Learn more ↗" : "Get action plan ↗"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Deal header
// ---------------------------------------------------------------------------

interface DealHeaderProps {
  deal: AccountFragmentFragment["deals"][0] | undefined;
  low: number | null;
  mid: number | null;
  high: number | null;
  multLow: number | null;
  multHigh: number | null;
  industryMultLow: number | null;
  industryMultHigh: number | null;
  onboardingStep: TteDealOnboardingStep | undefined;
  readinessScore: number;
  allHighAnswered: boolean;
  valuationStatus: TteValuationStatus | null;
}

function DealHeader({
  deal,
  low,
  mid,
  high,
  multLow,
  multHigh,
  industryMultLow,
  industryMultHigh,
  onboardingStep,
  readinessScore,
  allHighAnswered,
  valuationStatus,
}: DealHeaderProps) {
  const hasLiveValuation = low !== null && mid !== null && high !== null;
  const showValuation = hasLiveValuation && onboardingStep !== TteDealOnboardingStep.Step_1;
  const showReadiness = onboardingStep === TteDealOnboardingStep.Step_3 || onboardingStep === undefined;
  const showIndustry =
    showValuation &&
    multLow !== null &&
    multHigh !== null &&
    industryMultLow !== null &&
    industryMultHigh !== null;

  const label = showReadiness ? readinessLabel(readinessScore) : "SETTING UP";
  const ringProgress = showReadiness ? readinessScore : 0;

  // Animated valuation numbers
  const aLow = useAnimatedNumber(low);
  const aMid = useAnimatedNumber(mid);
  const aHigh = useAnimatedNumber(high);
  const aMultLow = useAnimatedNumber(multLow);
  const aMultHigh = useAnimatedNumber(multHigh);

  // Valuation position vs industry
  type IndustryStatus = { sentiment: "positive" | "neutral" | "negative"; title: string; subtitle: string };
  const industryStatus: IndustryStatus | null = (() => {
    if (!showIndustry) return null;
    if (multLow! >= industryMultHigh!) return { sentiment: "positive", title: "Above industry standard", subtitle: `Your deal multiple sits above the industry benchmark of ${industryMultLow!.toFixed(1)}x–${industryMultHigh!.toFixed(1)}x` };
    if (multLow! < industryMultLow!) return { sentiment: "negative", title: "Below industry standard", subtitle: `Part of your deal range falls below the industry benchmark of ${industryMultLow!.toFixed(1)}x–${industryMultHigh!.toFixed(1)}x` };
    return { sentiment: "neutral", title: "In line with industry", subtitle: `Your deal multiple sits within the industry benchmark of ${industryMultLow!.toFixed(1)}x–${industryMultHigh!.toFixed(1)}x` };
  })();

  const dealBarSentiment: DealBarSentiment =
    industryStatus === null ? "default"
    : industryStatus.sentiment === "negative" ? "negative"
    : "positive";

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="grid grid-cols-3 gap-0 divide-x divide-gray-100">
        {/* Column 1: Readiness ring + status summaries */}
        <div className="flex items-center gap-5 pr-6">
          {/* Ring */}
          <div className="flex-shrink-0">
            <div className="relative w-24 h-24">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#ede9fe" strokeWidth="2.5" />
                {showReadiness && (
                  <circle
                    cx="18" cy="18" r="15.9" fill="none" stroke="#7c3aed" strokeWidth="2.5"
                    strokeDasharray={`${ringProgress} ${100 - ringProgress}`}
                    strokeLinecap="round"
                  />
                )}
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                {showReadiness ? (
                  <>
                    <span className="text-xl font-bold text-persian-900 leading-none">{readinessScore}</span>
                    <span className="text-[10px] text-gray-400 mt-0.5">of 100</span>
                  </>
                ) : (
                  <span className="text-xs text-gray-300">—</span>
                )}
              </div>
            </div>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mt-2 text-center">
              Readiness
            </p>
            <div className="flex justify-center mt-1">
              <span className={`text-xs font-semibold rounded-full px-2.5 py-0.5 ${
                showReadiness ? "bg-persian-100 text-persian-700" : "bg-gray-100 text-gray-400"
              }`}>
                {label}
              </span>
            </div>
          </div>

          {/* Status summaries */}
          <div className="flex-1 space-y-3">
            {/* Information status */}
            <div className="flex items-start gap-2.5">
              {allHighAnswered ? (
                <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <ExclamationCircleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className={`text-sm font-semibold leading-tight ${allHighAnswered ? "text-green-800" : "text-gray-800"}`}>
                  {allHighAnswered ? "Key information provided" : "Need more information"}
                </p>
                <p className="text-xs text-gray-400 mt-0.5 leading-snug">
                  {allHighAnswered
                    ? "You've answered the key questions needed for an accurate valuation."
                    : "Provide more documents and answers to get an accurate valuation."}
                </p>
              </div>
            </div>

            {/* Valuation status */}
            {industryStatus ? (
              <div className="flex items-start gap-2.5">
                {industryStatus.sentiment === "negative" ? (
                  <ExclamationCircleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <p className={`text-sm font-semibold leading-tight ${
                    industryStatus.sentiment === "negative" ? "text-amber-800" : "text-green-800"
                  }`}>
                    {industryStatus.title}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 leading-snug">{industryStatus.subtitle}</p>
                </div>
              </div>
            ) : showReadiness ? (
              <div className="flex items-start gap-2.5">
                <InformationCircleIcon className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-gray-400 leading-tight">Valuation pending</p>
                  <p className="text-xs text-gray-300 mt-0.5 leading-snug">Complete setup to receive your valuation.</p>
                </div>
              </div>
            ) : null}

            {deal?.company?.name && (
              <p className="text-xs text-gray-400 truncate">{deal.company.name}</p>
            )}
          </div>
        </div>

        {/* Column 2: Valuation */}
        <div className="px-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              Your Valuation
            </p>
            {valuationStatus === TteValuationStatus.Calculating && (
              <span className="text-[10px] text-gray-400 animate-pulse">Recalculating…</span>
            )}
          </div>
          {showValuation ? (
            <>
              <p className="text-xl font-bold text-gray-900 tabular-nums">
                {fmtM(aLow ?? low!)} – {fmtM(aHigh ?? high!)}
              </p>
              <p className="text-sm text-gray-500 mt-0.5 tabular-nums">Midpoint {fmtM(aMid ?? mid!)}</p>
              {multLow !== null && multHigh !== null && (
                <>
                  <p className="text-xs text-gray-400 mt-3 tabular-nums">
                    EBITDA multiple {(aMultLow ?? multLow).toFixed(1)}x – {(aMultHigh ?? multHigh).toFixed(1)}x
                  </p>
                  <EbitdaBar
                    multLow={aMultLow ?? multLow}
                    multHigh={aMultHigh ?? multHigh}
                    sentiment={dealBarSentiment}
                  />
                </>
              )}
            </>
          ) : (
            <>
              <p className="text-xl font-bold text-gray-200">· · · · · ·</p>
              <p className="text-xs text-gray-300 mt-1">
                {onboardingStep === TteDealOnboardingStep.Step_1
                  ? "Complete setup to unlock"
                  : "Calculating…"}
              </p>
            </>
          )}
        </div>

        {/* Column 3: VS. Industry */}
        <div className="pl-6">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">
            VS. Industry
          </p>
          {showIndustry ? (
            <IndustryComparisonBars
              industryLow={industryMultLow!}
              industryHigh={industryMultHigh!}
              dealLow={aMultLow ?? multLow!}
              dealHigh={aMultHigh ?? multHigh!}
              sentiment={dealBarSentiment}
            />
          ) : (
            <p className="text-xs text-gray-300 mt-2">
              {showValuation ? "No industry benchmark" : "Available after setup"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Question card
// ---------------------------------------------------------------------------

function QuestionCard({
  q,
  onAnswer,
}: {
  q: SectionQuestion;
  onAnswer: (q: SectionQuestion) => void;
}) {
  const isAnswered = (q.answers?.length ?? 0) > 0;
  const priorityStyle = PRIORITY_STYLES[q.priority ?? ""];

  return (
    <div className="flex items-start justify-between gap-3 py-2.5 border-b border-gray-50 last:border-0">
      <div className="flex items-start gap-2.5 min-w-0">
        {isAnswered ? (
          <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
        ) : (
          <div className="w-4 h-4 rounded-full border-2 border-gray-200 flex-shrink-0 mt-0.5" />
        )}
        <div className="min-w-0">
          <p className="text-sm text-gray-800 leading-snug">{q.title}</p>
          {q.priority && q.priority !== QuestionPriority.None && priorityStyle && (
            <span className={`inline-block mt-1 text-xs font-semibold px-2 py-0.5 rounded-full ${priorityStyle.badge}`}>
              {q.priority.toUpperCase()}
            </span>
          )}
        </div>
      </div>
      <Button
        text={isAnswered ? "Add answer" : "Answer"}
        variant="neutral/soft"
        size="xs"
        onClick={() => onAnswer(q)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Document request item
// ---------------------------------------------------------------------------

function DocumentItem({
  doc,
  onUpload,
}: {
  doc: SectionQuestion;
  onUpload: (q: SectionQuestion) => void;
}) {
  const isAnswered = (doc.answers?.length ?? 0) > 0;

  return (
    <div className="flex items-center justify-between gap-3 bg-gray-50 rounded-lg px-3 py-2.5">
      <div className="flex items-center gap-2.5 min-w-0">
        {isAnswered ? (
          <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
        ) : (
          <div className="w-4 h-4 rounded border border-gray-300 bg-white flex-shrink-0" />
        )}
        <span className="text-sm text-gray-700 truncate">{doc.title}</span>
      </div>
      <Button
        text={isAnswered ? "Add file" : "Upload"}
        variant="neutral/soft"
        size="xs"
        icon={UploadIcon}
        onClick={() => onUpload(doc)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Readiness card (DDQL section)
// ---------------------------------------------------------------------------

function SectionProgressRing({ progress, complete }: { progress: number; complete: boolean }) {
  const size = 20;
  const stroke = 2.5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - Math.max(0, Math.min(1, progress)));
  const color = complete ? "text-green-500" : "text-persian-500";
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        strokeWidth={stroke}
        className="text-gray-200"
        stroke="currentColor"
        fill="none"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        strokeWidth={stroke}
        className={`${color} transition-all duration-500 ease-out`}
        stroke="currentColor"
        fill="none"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
      />
    </svg>
  );
}

function ReadinessCard({
  section,
  rootFolderId,
  onAnswered,
}: {
  section: Section;
  rootFolderId: string | undefined;
  onAnswered: () => void;
}) {
  const [selectedQuestion, setSelectedQuestion] = useState<SectionQuestion | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  function openAnswer(q: SectionQuestion) {
    setSelectedQuestion(q);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setSelectedQuestion(null);
  }

  function handleDone() {
    closeModal();
    onAnswered();
  }

  const docs = [...section.questions]
    .filter((q) => q.type === QuestionType.DocumentRequest)
    .sort((a, b) => (a.index ?? "").localeCompare(b.index ?? ""));

  const questions = [...section.questions]
    .filter((q) => q.type === QuestionType.Question)
    .sort((a, b) => (a.index ?? "").localeCompare(b.index ?? ""));

  const answeredDocs = docs.filter((d) => (d.answers?.length ?? 0) > 0).length;
  const answeredQs = questions.filter((q) => (q.answers?.length ?? 0) > 0).length;
  const totalItems = docs.length + questions.length;
  const answeredItems = answeredDocs + answeredQs;
  const progress = totalItems > 0 ? answeredItems / totalItems : 0;
  const complete = totalItems > 0 && answeredItems === totalItems;

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <h3 className="font-bold text-gray-900">{section.name}</h3>
          </div>
          {totalItems > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 tabular-nums">
                {answeredItems}/{totalItems}
              </span>
              <SectionProgressRing progress={progress} complete={complete} />
            </div>
          )}
        </div>

        <div className="px-5 py-4 space-y-5">
          {docs.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Documents
              </p>
              <div className="space-y-2">
                {docs.map((doc) => (
                  <DocumentItem key={doc.id} doc={doc} onUpload={openAnswer} />
                ))}
              </div>
            </div>
          )}

          {questions.length > 0 && (
            <div>
              {docs.length > 0 && <div className="border-t border-gray-100 mb-4" />}
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                Questions
              </p>
              <div>
                {questions.map((q) => (
                  <QuestionCard key={q.id} q={q} onAnswer={openAnswer} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <AnswerModal
        question={selectedQuestion}
        rootFolderId={rootFolderId}
        open={modalOpen}
        onClose={closeModal}
        onDone={handleDone}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Card skeleton
// ---------------------------------------------------------------------------

function CardSkeleton() {
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden animate-pulse">
      <div className="px-5 py-4 border-b border-gray-100">
        <div className="h-4 bg-gray-100 rounded w-1/3" />
      </div>
      <div className="px-5 py-4 space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-3 bg-gray-100 rounded" />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const Seller = () => {
  const { dealId } = useParams<{ dealId: string }>();
  const dispatch = useDispatch();
  const account = useSelector(authSelectors.account);
  const hasDeals = (account?.deals?.length ?? 0) > 0;
  const deal = account?.deals?.find((d) => d.id === dealId) ?? account?.deals?.[0];
  const client = useGqlClient();

  const accountQuery = useAccountQuery(
    client,
    {},
    {
      refetchInterval: hasDeals ? false : 1000,
    },
  );

  useEffect(() => {
    if (accountQuery.data) {
      dispatch(actions.refreshAccount({ account: accountQuery.data.account }));
    }
  }, [accountQuery.data, dispatch]);

  const dashboardQuery = useSellerDashboardQuery(client, { dealId });
  const drFastQuery = useDataRoomFastQuery(client, { dealId });
  const tteQuery = useTteDealDataQuery(client, { dealId }, {
    refetchInterval: (query) => {
      const status = query.state.data?.tteDealData?.valuationStatus;
      return status === TteValuationStatus.Calculating ? 1000 : 2000;
    },
  });

  const rootFolderId = drFastQuery.data?.deal?.dataRoom?.folder?.id;
  const sellerRequestList = dashboardQuery.data?.deal?.sellerRequestList;

  const tte = tteQuery.data?.tteDealData;
  const valuationStatus = tte?.valuationStatus ?? null;
  const adjustments: DealAdjustment[] = tte?.adjustments ?? [];
  const valuation = tte?.latestValuation;
  const ebitda = valuation?.input.ebitda ?? tte?.ebitda ?? null;

  const sections: Section[] = sellerRequestList
    ? [...sellerRequestList.children]
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    : [];

  const ddqlRatio = computeWeightedDdqlRatio(sections);
  const highPriorityRatio = computeHighPriorityRatio(sections);
  const readinessScore = computeReadinessScore({
    ddqlRatio,
    highPriorityRatio,
    valuation: valuation ?? null,
    ebitda,
    industry: tte?.industry ?? null,
  });
  const low = valuation?.output.low ?? null;
  const mid = valuation?.output.mid ?? null;
  const high = valuation?.output.high ?? null;
  const multLow = ebitda && low ? low / ebitda : null;
  const multHigh = ebitda && high ? high / ebitda : null;
  const industryMultLow = tte?.industry?.multipleLow ?? null;
  const industryMultHigh = tte?.industry?.multipleHigh ?? null;

  const onboardingStep = tte?.onboardingStep;
  const isOnboarding =
    onboardingStep === TteDealOnboardingStep.Step_1 ||
    onboardingStep === TteDealOnboardingStep.Step_2;

  const allHighAnswered =
    sections.length > 0 &&
    sections.every((section) =>
      section.questions
        .filter((q) => q.priority === QuestionPriority.High || q.priority === QuestionPriority.Urgent)
        .every((q) => (q.answers?.length ?? 0) > 0)
    );

  return (
    <div className="flex-1 overflow-y-auto bg-persian-50 p-8">
      <DealHeader
        deal={deal}
        low={low}
        mid={mid}
        high={high}
        multLow={multLow}
        multHigh={multHigh}
        industryMultLow={industryMultLow}
        industryMultHigh={industryMultHigh}
        onboardingStep={onboardingStep}
        readinessScore={readinessScore}
        allHighAnswered={allHighAnswered}
        valuationStatus={valuationStatus}
      />
      <div className="mt-4">
        <KeyFactorsCard adjustments={adjustments} />
      </div>

      {tteQuery.isLoading ? null : isOnboarding ? (
        <div className="mt-6 max-w-2xl mx-auto space-y-6">
          <OnboardingProgressBar step={onboardingStep === TteDealOnboardingStep.Step_1 ? 1 : 2} />
          {onboardingStep === TteDealOnboardingStep.Step_1 ? (
            <Step1Form tteId={tte!.id} onDone={() => tteQuery.refetch()} />
          ) : (
            <Step2Form tteId={tte!.id} onDone={() => tteQuery.refetch()} />
          )}
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {dashboardQuery.isLoading ? (
            <>
              {[1, 2, 3, 4, 5, 6].map((i) => <CardSkeleton key={i} />)}
            </>
          ) : !sellerRequestList ? (
            <div className="col-span-3 text-center py-16 text-gray-400">
              <p className="text-sm">No readiness list configured for this deal.</p>
            </div>
          ) : (
            sections.map((section) => (
              <ReadinessCard
                key={section.id}
                section={section}
                rootFolderId={rootFolderId}
                onAnswered={() => { dashboardQuery.refetch(); tteQuery.refetch(); }}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};
